import React, { useState, useEffect, useRef } from 'react'
import { animated, useSpring, config } from 'react-spring'
import moment from 'moment'

import { Select } from '../../common/components/select'
import { Button } from '../../common/components/button'
import { Slider } from '../../common/components/slider'
import { Switch } from '../../common/components/switch'
import { useReportPageContext } from '../contexts/report-page.context'
import { FilterBar } from './filter-bar'
import { reportLayoutService } from './report-layout.service'

import { useStyles } from './control-bar.style'

export const FILTER_SIZES = {
  MEDIUM: 'medium',
  LARGE: 'large'
}

const SLIDER_VALUES = [
  { value: 0, label: '0%' },
  { value: 0.2, label: '20%' },
  { value: 0.4, label: '40%' },
  { value: 0.6, label: '60%' },
  { value: 0.8, label: '80%' },
  { value: 1, label: '100%' }
]

export const ControlBar = function ({
  className = '',
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  tableToggle = { enabled: false, on: false },
  alwaysShowSelectDropdown = false,
  onChange = function () {}
}) {
  const classes = useStyles()

  const [primaryFilter, setPrimaryFilter] = useState(getPrimaryFilter(filterDefinition))
  const [filters, setFilters] = useState()
  const [isFilterButtonToggled, setIsFilterButtonToggled] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const [isFilterChangePending, setIsFilterChangePending] = useState(false)
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const reportPageContext = useReportPageContext()
  const parentControlBarState = getParentControlBarState()

  const useDeprecatedDisplayProp = primaryFilter && primaryFilter.useDeprecatedDisplayProp
  const selectedPrimaryFilter = useRef()
  const selectedStrata = useRef(reportLayoutService.getDefaultValue(strataDefinition))
  const selectedFilters = useRef({})
  const selectedSliderValue = useRef(reportLayoutService.getDefaultValue(sliderDefinition))
  const chartPropOverrides = useRef({})
  const isTableToggleOn = useRef(tableToggle.on)
  const filterContainerRef = useRef()

  const thresholdFilters = filterDefinition && filterDefinition.thresholdFilters
  const thresholdFilter = thresholdFilters && thresholdFilters.fields && thresholdFilters.fields[0] // Currently, only one is supported but it could be more in the future.
  const [selectedThresholdFilter, setSelectedThresholdFilter] = useState(reportLayoutService.getDefaultValue(thresholdFilter))
  const [selectedThresholdValue, setSelectedThresholdValue] = useState(SLIDER_VALUES[0].value)

  const filterContainerStyles = useSpring({
    config: { ...config.stiff, clamp: true },
    from: { opacity: 0, height: 0 },
    to: {
      opacity: isFilterButtonToggled ? 1 : 0,
      height: (isFilterButtonToggled && filterContainerRef.current) ? filterContainerRef.current.offsetHeight : 0
    }
  })

  function clearFilters (isStrataChange = false) {
    setIsFilterApplied(false)
    setIsFilterChangePending(false)
    setIsFilterButtonToggled(false)
    initializeFilters()
    callOnChange({ strata: isStrataChange, filters: true })
  }

  function clearThresholdFilter () {
    const defaultFilter = reportLayoutService.getDefaultValue(thresholdFilter)
    const defaultValue = SLIDER_VALUES[0].value
    setSelectedThresholdFilter(defaultFilter)
    setSelectedThresholdValue(defaultValue)
    callOnChange({ threshold: true }, {
      threshold: { key: defaultFilter, value: defaultValue }
    })
  }

  function handleApply () {
    setIsFilterApplied(true)
    setIsFilterChangePending(false)
    callOnChange({ filters: true })
  }

  function callOnChange ({ display = false, strata = false, filters = false, slider = false, threshold = false, props = false, tableToggle = false }, selectionOverrides = {}) {
    // If we are expecting parent selections, but have not received them yet, hold off on calling onChange
    if ((parentControlBar && !parentControlBarState) || (parentBlock && !parentBlockSelections)) {
      return
    }
    const selections = Object.assign(getSelections(), selectionOverrides)
    const changes = { display, strata, filters, slider, threshold, props, tableToggle }

    onChange(selections, changes)
  }

  function updateFilters (key, value) {
    const updatedFilters = filters.map((filter) => {
      if (filter.key === key) {
        filter.selectedValue = value
      }
      return filter
    })
    setFilters(updatedFilters)
    setIsFilterChangePending(true)

    selectedFilters.current[key] = value
  }

  function getPrimaryFilter () {
    if (!filterDefinition || !Array.isArray(filterDefinition.fields)) {
      return
    }

    return filterDefinition.fields.find(({ isPrimary }) => isPrimary)
  }

  function getFiltersWithDefaultValues () {
    if (!filterDefinition || !Array.isArray(filterDefinition.fields)) {
      return
    }

    const updatedFilters = filterDefinition.fields
      .filter(({ isPrimary, isSplitChartFilter }) => !isPrimary && !isSplitChartFilter)
      .map((filter) => {
        // Reset all filters to their default value
        filter.selectedValue = getDefaultFilterValue(filter)
        return filter
      })

    // Hide the filter if needed
    return updatedFilters.filter((filter) => {
      // Hide if a strata or primary filter condition is specified
      if (filter.hideCondition) {
        if ((filter.hideCondition.strata || []).includes(selectedStrata.current)) {
          return false
        }
        if ((filter.hideCondition.primaryFilter || []).includes(selectedPrimaryFilter.current)) {
          return false
        }
      }
      // Hide if stratifying by the same field as this filter
      return (filter.key !== selectedStrata.current)
    })
  }

  function getDefaultFilterValue (filter) {
    if (!filter) {
      return
    }

    // Check if the parent has sent in a value and default to that
    const parentFilterValue = ((parentBlockSelections || {}).filters || {})[filter.key]

    return (parentFilterValue !== undefined)
      ? parentFilterValue
      : reportLayoutService.getDefaultValue(filter)
  }

  function updateChartProps () {
    if (selectedPrimaryFilter.current) {
      const selectedOption = primaryFilter.options.find(({ value }) => value === selectedPrimaryFilter.current)
      if (selectedOption && selectedOption.propOverrides) {
        chartPropOverrides.current = reportLayoutService.applyPropOverrides(chartPropOverrides.current, selectedOption.propOverrides)
      }
    }
  }

  function handleTableToggleChange (event) {
    if (event && event.target) {
      isTableToggleOn.current = event.target.checked
      callOnChange({ tableToggle: true })
    }
  }

  function hasChartPropOverrides () {
    if (!primaryFilter || !Array.isArray(primaryFilter.options)) {
      return false
    }

    const selectedOption = primaryFilter.options.find(({ value }) => value === selectedPrimaryFilter.current)
    return selectedOption && selectedOption.propOverrides
  }

  function getParentControlBarState () {
    if (!parentControlBar || !reportPageContext || !reportPageContext.getControlBarState) {
      return null
    }

    return reportPageContext.getControlBarState(parentControlBar.key)
  }

  /**
   * Only filters on parent selections are supported - strata, slider, etc. can be pulled from parent later if needed
   */
  function getParentSelections () {
    let parentSelections = { filters: {} }

    // get parent control bar filters
    if (parentControlBar && parentControlBarState) {
      if (parentControlBar.filters && parentControlBarState.selections) {
        parentControlBar.filters.forEach((filter) => {
          const filterSelection = parentControlBarState.selections.filters[filter]
          if (filterSelection !== undefined) {
            parentSelections.filters[filter] = filterSelection
          }
        })
      }
    }

    // get parent block filters (overrides parent control bar if there are conflicts)
    if (parentBlock && parentBlockSelections) {
      if (parentBlockSelections.filters) {
        parentSelections.filters = { ...parentSelections.filters, ...parentBlockSelections.filters }
      }
    }

    return parentSelections
  }

  function getSelections () {
    const parent = getParentSelections()

    const parentFilters = parent.filters || {}
    // Selected filters override parent filters if there are conflicts
    const filters = { ...parentFilters, ...selectedFilters.current }

    // Add the primary filter value to filters (unless this is the legacy display type filter)
    // Add the primary filter value to filters (unless this is the legacy display type filter)
    if (primaryFilter && !useDeprecatedDisplayProp) {
      filters[primaryFilter.key] = selectedPrimaryFilter.current
    }

    return {
      display: useDeprecatedDisplayProp ? selectedPrimaryFilter.current : undefined,
      strata: selectedStrata.current,
      filters,
      threshold: { key: selectedThresholdFilter, value: selectedThresholdValue },
      refDataSource: thresholdFilter && thresholdFilter.dataSource,
      slider: selectedSliderValue.current,
      props: { ...chartPropOverrides.current },
      tableToggle: { on: isTableToggleOn.current }
    }
  }

  function initializePrimaryFilter () {
    const primaryFilter = getPrimaryFilter(filterDefinition)
    if (primaryFilter) {
      primaryFilter.selectedValue = getDefaultFilterValue(primaryFilter)
      selectedPrimaryFilter.current = primaryFilter.selectedValue
      setPrimaryFilter(primaryFilter)
    }
  }

  function initializeFilters () {
    const filters = getFiltersWithDefaultValues()
    if (filters) {
      const initializedFilters = {}
      filters.forEach((f) => {
        if (f.selectedValue !== undefined) {
          initializedFilters[f.key] = f.selectedValue
        }
      })
      selectedFilters.current = initializedFilters

      setFilters(filters)
    }
  }

  useEffect(() => {
    initializePrimaryFilter()
    initializeFilters()
    updateChartProps()
    callOnChange({
      display: useDeprecatedDisplayProp && !!selectedPrimaryFilter.current,
      filters: !!selectedFilters.current || (!useDeprecatedDisplayProp && !!selectedPrimaryFilter.current),
      strata: !!selectedStrata.current,
      props: hasChartPropOverrides()
    })
  }, [])

  useEffect(() => {
    if (parentControlBarState) {
      callOnChange(parentControlBarState.changes || {})
    }
  }, [ parentControlBarState ])

  useEffect(() => {
    if (parentBlockSelections) {
      const parentChanges = Object.keys(parentBlockSelections)
        .reduce((parentChanges, key) => ({ ...parentChanges, [key]: true }), {})
      callOnChange(parentChanges)
    }
  }, [ parentBlockSelections ])

  useEffect(() => {
    isTableToggleOn.current = tableToggle.on
  }, [ tableToggle.on ])

  useEffect(() => {
    if (!filters || !filterDefinition || !filterDefinition.fields || filterDefinition.fields.length === 0) {
      return
    }

    const previouslySelectedPrimaryFilter = selectedPrimaryFilter.current
    const previouslySelectedFilters = { ...selectedFilters.current }
    initializePrimaryFilter()
    initializeFilters()

    let hasFiltersChanged = false
    if (Object.keys(previouslySelectedFilters).length === Object.keys(selectedFilters.current).length) {
      for (let key in previouslySelectedFilters) {
        if (previouslySelectedFilters[key] !== selectedFilters.current[key]) {
          hasFiltersChanged = true
        }
      }
    } else {
      hasFiltersChanged = true
    }

    if (hasFiltersChanged || (previouslySelectedPrimaryFilter !== selectedPrimaryFilter.current)) {
      callOnChange({
        filters: true
      })
    }
  }, [ filterDefinition ])

  const hasMultipleStrata = (strataDefinition && strataDefinition.options && strataDefinition.options.length > 1)
  const hasMultiplePrimaryFilterOptions = primaryFilter && primaryFilter.options && (primaryFilter.options.filter(({ hide }) => (!hide)).length > 1)
  const shouldDisplayFilters = filters && !!filters.length && filters.some((filter) => (alwaysShowSelectDropdown || filter.options.filter(({ hide }) => (!hide)).length > 1))

  if (isFilterVisible !== shouldDisplayFilters) {
    setIsFilterVisible(shouldDisplayFilters)
  }

  if (!reportPageContext.page.isInteractive) {
    return null
  } else if (primaryFilter || hasMultipleStrata || sliderDefinition) {
    return (
      <div data-qa="control-bar" className={className}>
        <div className={classes.container}>
          {hasMultiplePrimaryFilterOptions &&
            <div className={classes.select} data-qa="control-bar-display-filter">
              <Select
                label={primaryFilter.label || 'Display'}
                options={primaryFilter.options.filter(({ hide }) => (!hide))}
                value={primaryFilter.options.find(({ value }) => value === selectedPrimaryFilter.current)}
                onChange={({ value }) => {
                  selectedPrimaryFilter.current = value
                  updateChartProps()
                  callOnChange({
                    display: useDeprecatedDisplayProp,
                    filters: !useDeprecatedDisplayProp,
                    props: hasChartPropOverrides()
                  })
                }} />
            </div>
          }
          {hasMultipleStrata &&
            <div className={classes.select} data-qa="control-bar-stratify-by">
              <Select
                label="Stratify By"
                options={strataDefinition.options}
                value={strataDefinition.options.find(({ value }) => value === selectedStrata.current)}
                onChange={({ value }) => {
                  selectedStrata.current = value
                  clearFilters(true)
                }} />
            </div>
          }
          {sliderDefinition && sliderDefinition.options &&
            <div className={classes.sliderContainer}>
              <label className="form-element-label">{sliderDefinition.label}</label>
              <Slider
                dateMode={sliderDefinition.dateMode}
                dateValueFormat={sliderDefinition.dateValueFormat}
                value={selectedSliderValue.current}
                min={reportLayoutService.getMinValue(sliderDefinition)}
                max={reportLayoutService.getMaxValue(sliderDefinition)}
                marks={reportLayoutService.getSliderMarkerPoints(sliderDefinition)}
                onChange={(event, value) => {
                  // onChange is called on every mouse move
                  value = sliderDefinition.dateMode ? moment.utc(value).toISOString() : value
                  if (value !== selectedSliderValue.current) {
                    selectedSliderValue.current = value
                    callOnChange({ slider: true })
                  }
                }} />
            </div>
          }
          {(tableToggle.enabled || isFilterVisible || (thresholdFilters && thresholdFilter)) &&
            <div className={classes.endSection}>
              { tableToggle.enabled && (
                <div className={classes.switchOnPrimaryRow}>
                  <TableToggle isOn={tableToggle.on} onChange={handleTableToggleChange} />
                </div>
              )}
              {isFilterVisible &&
                <Button data-qa="control-bar-filter-dropdown" variant="secondary" size="large" className={isFilterButtonToggled ? 'active' : ''}
                  onClick={(event) => {
                    isFilterButtonToggled ? clearFilters(false) : setIsFilterButtonToggled(true)
                    event.currentTarget.blur() // If we do not blur, the button will look like it is toggle when it is not.
                  }}>
                  {isFilterApplied ? 'Clear Filter' : 'Filter'}
                  <span className={`font-icon-down-caret ${classes.downCaret}`}></span>
                </Button>
              }
              {thresholdFilters && thresholdFilter &&
                <Button variant="secondary" size="large" className={isFilterButtonToggled ? 'active' : ''}
                  onClick={(event) => {
                    setIsFilterButtonToggled(!isFilterButtonToggled)
                    clearThresholdFilter()
                    event.currentTarget.blur() // If we do not blur, the button will look like it is toggle when it is not.
                  }}>
                  {isFilterButtonToggled ? (thresholdFilters && thresholdFilters.toggledButtonLabel) || 'Clear Filter' : (thresholdFilters && thresholdFilters.buttonLabel) || 'Filter'}
                  <span className={`font-icon-down-caret ${classes.downCaret}`}></span>
                </Button>
              }
            </div>
          }
        </div>
        {isFilterVisible && (
          <animated.div style={{
            ...filterContainerStyles,
            overflow: isFilterButtonToggled ? 'visible' : 'hidden' // need to make overflow visible once animation completes so that dropdowns does not go underneath
          }}>
            <div ref={filterContainerRef} className={classes.filterContainer}>
              <FilterBar filters={filters} size={FILTER_SIZES.MEDIUM} onChange={updateFilters} width={924} alwaysShowSelectDropdown={alwaysShowSelectDropdown} />
              <Button
                data-qa="apply-filter-changes"
                variant="secondary"
                className={classes.applyButton}
                onClick={handleApply}
                disabled={!isFilterChangePending}
              >
                  Apply
              </Button>
            </div>
          </animated.div>
        )}
        {thresholdFilters && thresholdFilter && (
          <animated.div style={{
            ...filterContainerStyles,
            overflow: isFilterButtonToggled ? 'visible' : 'hidden' // need to make overflow visible once animation completes so that dropdowns does not go underneath
          }}>
            <div ref={filterContainerRef} className={classes.filterContainer}>
              <div className={classes.sliderContainer}>
                <Select
                  size={FILTER_SIZES.LARGE}
                  label={thresholdFilter.label}
                  options={thresholdFilter.options}
                  value={thresholdFilter.options.find(({ value }) => value === selectedThresholdFilter)}
                  onChange={({ value }) => {
                    const defaultValue = SLIDER_VALUES[0].value
                    setSelectedThresholdValue(defaultValue)
                    setSelectedThresholdFilter(value)
                    callOnChange({ threshold: true }, {
                      threshold: { key: value, value: defaultValue }
                    })
                  }}
                />
              </div>
              <div className={classes.sliderContainer}>
                <label className="form-element-label">{thresholdFilter.sliderLabel}</label>
                <Slider
                  value={selectedThresholdValue}
                  min={0}
                  max={1}
                  marks={SLIDER_VALUES}
                  step={0.01}
                  track="inverted"
                  onChange={(event, value) => {
                    // onChange is called on every mouse move
                    if (value !== selectedThresholdValue) {
                      setSelectedThresholdValue(value)
                    }
                  }}
                  onChangeCommitted={(event, value) => {
                    callOnChange({ threshold: true })
                  }}
                  valueLabelFormat={(value) => {
                    return `${Math.round(value * 100)}%`
                  }} />
              </div>
            </div>
          </animated.div>
        )}
      </div>
    )
  } else if (tableToggle.enabled || isFilterVisible) {
    return (
      <div className={`${className} ${classes.standaloneFilterRow}`} data-qa="control-bar">
        {isFilterVisible && <>
          <FilterBar filters={filters} size={FILTER_SIZES.MEDIUM} onChange={updateFilters} width={tableToggle.enabled ? 765 : 924} alwaysShowSelectDropdown={alwaysShowSelectDropdown} />
          <Button
            data-qa="apply-filter-changes"
            variant="secondary"
            className={classes.applyButton}
            onClick={handleApply}
            disabled={!isFilterChangePending}
          >
            Apply
          </Button>
        </>}
        {tableToggle.enabled && (
          <div className={classes.switchOnFilterRow}>
            <TableToggle isOn={tableToggle.on} onChange={handleTableToggleChange} />
          </div>
        )}
      </div>
    )
  } else {
    return null
  }
}

function TableToggle ({ isOn, onChange }) {
  return <div data-qa="control-bar-table-toggle"><Switch isOn={isOn} onToggle={onChange} offLabel="Show Table" onLabel="Show Chart" /></div>
}
